import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { SectionHeading } from "@/components/primitives";
import { Monitor, Smartphone, Feather, RefreshCcw, Layers, Rocket, MousePointerClick } from "lucide-react";
import { Link } from "react-router-dom";

const BENEFITS = [
  { icon: Feather, title: "Lightweight", desc: "App-like access without a bulky traditional installation." },
  { icon: RefreshCcw, title: "Always Current", desc: "Product updates arrive through the web — no manual downloads." },
  { icon: Layers, title: "One Connected Experience", desc: "Move between browser, desktop and mobile with the same Workspace and data." },
  { icon: Rocket, title: "Quick Access", desc: "Launch from your desktop or mobile home screen like an app." },
  { icon: MousePointerClick, title: "Less Duplication", desc: "Installed and browser experiences share the same underlying WorkSpace." },
];

export default function PwaSection() {
  return (
    <section className="relative border-t border-border py-24 sm:py-32" data-testid="pwa-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Anywhere You Work"
          title="Your WorkSpace. Wherever you work."
          subtitle="Open WorkSpace in your browser, or install it for app-like access across supported desktop and mobile devices."
        />

        <div className="mt-16 grid gap-8 lg:grid-cols-[1.3fr_1fr]">
          {/* Benefit grid */}
          <Stagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b) => {
              const Icn = b.icon;
              return (
                <StaggerItem key={b.title} className="h-full">
                  <div className="card-hover flex h-full flex-col gap-3 rounded-2xl border border-border bg-card p-5 hover:border-foreground/20">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-muted/60 text-brand-teal">
                      <Icn className="h-5 w-5" />
                    </span>
                    <h3 className="font-display text-base font-semibold tracking-tight">{b.title}</h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{b.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>

          {/* Install guide */}
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="install-guide">
              <p className="eyebrow text-brand-teal">Install WorkSpace</p>
              <h3 className="mt-2 font-display text-xl font-semibold tracking-tight">Add it to your home screen or desktop.</h3>

              <ol className="mt-5 space-y-4 text-sm">
                <li>
                  <div className="flex items-center gap-2 text-foreground">
                    <Smartphone className="h-4 w-4 text-brand-teal" /> <span className="font-semibold">Android</span>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    Open WorkSpace in Chrome → browser menu → <em>Install app</em> or <em>Add to Home screen</em>.
                  </p>
                </li>
                <li>
                  <div className="flex items-center gap-2 text-foreground">
                    <Smartphone className="h-4 w-4 text-brand-teal" /> <span className="font-semibold">iPhone / iPad</span>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    Open WorkSpace in Safari → tap <em>Share</em> → choose <em>Add to Home Screen</em>.
                  </p>
                </li>
                <li>
                  <div className="flex items-center gap-2 text-foreground">
                    <Monitor className="h-4 w-4 text-brand-teal" /> <span className="font-semibold">Windows / Mac</span>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    In supported browsers (Chrome, Edge, Arc) look for the <em>Install</em> option in the address bar or browser menu.
                  </p>
                </li>
              </ol>

              <Link
                to="/resources/install-workspace"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-teal hover:underline"
                data-testid="install-guide-link"
              >
                Full install guide →
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
